import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  StateCondition,
  SubCondition,
  HubOptionStateValue,
  ConditionQuantifier,
  HUB_MAX_SUB_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { createEmptySubCondition } from '../stateConditionDefaults';
import { SubConditionRow } from './SubConditionRow';
import {
  HUB_CONTINUE_DISABLED_TEXT_MAX_LENGTH,
  HUB_DISABLED_TEXT_MAX_LENGTH,
} from '../hubStepDefaults';
import './StateConditionCard.scss';

interface StateConditionCardProps {
  condition: StateCondition;
  memoryVariables: MemoryDTO[];
  isDisabled: boolean;
  isContinue: boolean;
  isValid: boolean;
  onChange: (updated: StateCondition) => void;
  onRemove: () => void;
}

const STATE_OPTIONS: { value: HubOptionStateValue; label: string }[] = [
  { value: HubOptionStateValue.Hidden, label: 'Hidden' },
  { value: HubOptionStateValue.Disabled, label: 'Disabled' },
  { value: HubOptionStateValue.Active, label: 'Active' },
];

const STATE_BADGE_CLASS: Record<HubOptionStateValue, string> = {
  [HubOptionStateValue.Hidden]: 'state-condition-card__badge--hidden',
  [HubOptionStateValue.Disabled]: 'state-condition-card__badge--disabled',
  [HubOptionStateValue.Active]: 'state-condition-card__badge--active',
};

export function StateConditionCard({
  condition,
  memoryVariables,
  isDisabled,
  isContinue,
  isValid,
  onChange,
  onRemove,
}: StateConditionCardProps) {
  const disabledTextMaxLength = isContinue
    ? HUB_CONTINUE_DISABLED_TEXT_MAX_LENGTH
    : HUB_DISABLED_TEXT_MAX_LENGTH;
  const disabledTextLength = condition.disabledText?.length ?? 0;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: condition.uuid,
    disabled: isDisabled,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: isDisabled ? undefined : CSS.Transform.toString(transform),
    transition: isDisabled ? undefined : transition,
    boxShadow: isDragging
      ? '-1px 0 15px 0 rgba(34, 33, 81, 0.01), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)'
      : undefined,
    opacity: isDragging ? 0.95 : 1,
  };

  // Defensive: ensure at least 1 sub-condition exists (corrupted data guard)
  const subConditions = condition.subConditions.length > 0
    ? condition.subConditions
    : [createEmptySubCondition()];
  const isCompound = subConditions.length >= 2;
  const canAddSub = subConditions.length < HUB_MAX_SUB_CONDITIONS;

  const handleQuantifierToggle = useCallback(
    (q: ConditionQuantifier) => {
      onChange({ ...condition, quantifier: q });
    },
    [condition, onChange],
  );

  const handleSubConditionChange = useCallback(
    (index: number, updated: SubCondition) => {
      const newSubs = [...subConditions];
      newSubs[index] = updated;
      onChange({ ...condition, subConditions: newSubs });
    },
    [condition, subConditions, onChange],
  );

  const handleSubConditionRemove = useCallback(
    (index: number) => {
      const newSubs = subConditions.filter((_, i) => i !== index);
      onChange({
        ...condition,
        subConditions: newSubs,
        // Reset quantifier to ALL when dropping to single sub-condition (AC-3.0)
        quantifier: newSubs.length <= 1 ? ConditionQuantifier.All : condition.quantifier,
      });
    },
    [condition, subConditions, onChange],
  );

  const handleAddSubCondition = useCallback(() => {
    onChange({
      ...condition,
      subConditions: [...subConditions, createEmptySubCondition()],
    });
  }, [condition, subConditions, onChange]);

  const handleResultStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const resultState = e.target.value as HubOptionStateValue;
      onChange({
        ...condition,
        resultState,
        disabledText: resultState === HubOptionStateValue.Disabled ? (condition.disabledText ?? '') : undefined,
      });
    },
    [condition, onChange],
  );

  const handleDisabledTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...condition, disabledText: e.target.value });
    },
    [condition, onChange],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`state-condition-card${isDragging ? ' state-condition-card--dragging' : ''}${!isValid ? ' state-condition-card--error' : ''}`}
      data-testid="state-condition-card"
    >
      {/* Header row: drag handle + condition header + remove */}
      <div className="state-condition-card__header">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          disabled={isDisabled}
        />

        {isCompound ? (
          <div className="state-condition-card__quantifier">
            <span className="state-condition-card__when-label">When</span>
            <div className="state-condition-card__logic-toggle">
              <button
                type="button"
                className={condition.quantifier === ConditionQuantifier.All ? 'active' : ''}
                onClick={() => handleQuantifierToggle(ConditionQuantifier.All)}
                disabled={isDisabled}
                data-testid="quantifier-all"
              >
                ALL
              </button>
              <button
                type="button"
                className={condition.quantifier === ConditionQuantifier.Any ? 'active' : ''}
                onClick={() => handleQuantifierToggle(ConditionQuantifier.Any)}
                disabled={isDisabled}
                data-testid="quantifier-any"
              >
                ANY
              </button>
            </div>
            <span className="state-condition-card__quantifier-text">of these are true:</span>
          </div>
        ) : (
          <div className="state-condition-card__single-label">
            <span>When</span>
          </div>
        )}

        <button
          type="button"
          className="btn btn-sm btn-outline-danger state-condition-card__remove"
          onClick={onRemove}
          disabled={isDisabled}
          title="Remove condition"
          data-testid="remove-condition"
        >
          ×
        </button>
      </div>

      {/* Sub-conditions */}
      <div className="state-condition-card__sub-conditions">
        {subConditions.map((sub, idx) => (
          <SubConditionRow
            key={sub.uuid}
            subCondition={sub}
            memoryVariables={memoryVariables}
            isDisabled={isDisabled}
            onChange={(updated) => handleSubConditionChange(idx, updated)}
            onRemove={() => handleSubConditionRemove(idx)}
            canRemove={subConditions.length > 1}
          />
        ))}

        <button
          type="button"
          className="btn btn-sm btn-outline-info state-condition-card__add-sub mt-2"
          onClick={handleAddSubCondition}
          disabled={isDisabled || !canAddSub}
          data-testid="add-sub-condition"
        >
          + Sub-condition
        </button>
      </div>

      {/* Result row */}
      <div className="state-condition-card__result">
        <span className="state-condition-card__result-arrow">→</span>
        <span className="state-condition-card__result-label">State</span>

        <Form.Control
          as="select"
          size="sm"
          className="state-condition-card__state-select"
          value={condition.resultState}
          onChange={handleResultStateChange}
          disabled={isDisabled}
          data-testid="state-select"
        >
          {STATE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Control>

        <span
          className={`state-condition-card__badge ${STATE_BADGE_CLASS[condition.resultState] ?? ''}`}
          data-testid="state-badge"
        >
          {condition.resultState.toUpperCase()}
        </span>
      </div>

      {/* Disabled text (only when resultState === 'disabled') */}
      {condition.resultState === HubOptionStateValue.Disabled && (
        <div className="state-condition-card__disabled-text" data-testid="disabled-text-section">
          <Form.Label className="state-condition-card__disabled-text-label">
            Explanation text (shown on disabled card)
          </Form.Label>
          <div className="state-condition-card__disabled-text-wrap">
            <Form.Control
              size="sm"
              type="text"
              placeholder="e.g. Complete the quest first"
              value={condition.disabledText ?? ''}
              onChange={handleDisabledTextChange}
              disabled={isDisabled}
              maxLength={disabledTextMaxLength}
              data-testid="disabled-text-input"
            />
            <Form.Text className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
              {disabledTextMaxLength - disabledTextLength}
            </Form.Text>
          </div>
        </div>
      )}
    </div>
  );
}
